import React, { useEffect, useState } from "react";
import axios from "axios";
import { useMessageModal } from "../admin/contexts/MessageModalContext";
import { useCurrencyConverter } from "../context/CurrencyConverterContext";
import "../styles/marketAnalysisPricing.css";

type MarketAnalysisPlan = {
  id: number;
  title: string;
  currency?: string;
  amount: number;
  search_charge_amount?: number;
  description: string;
  bullet_points?: string[] | string | null;
};

const API_BASE_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000";

const storedSubscriberUser = JSON.parse(
  localStorage.getItem("subscriber_user") || "null",
);

const MarketAnalysisPricing: React.FC = () => {
  const { showModal } = useMessageModal();

  const { targetCurrency, formatLocalCurrencyOnly } = useCurrencyConverter();

  const [plans, setPlans] = useState<MarketAnalysisPlan[]>([]);
  const [loading, setLoading] = useState(true);

  const getPlanBulletPoints = (plan: MarketAnalysisPlan) => {
    if (Array.isArray(plan.bullet_points)) {
      return plan.bullet_points.filter(Boolean);
    }

    if (typeof plan.bullet_points === "string") {
      try {
        const parsed = JSON.parse(plan.bullet_points);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch {
        return [];
      }
    }

    return [
      "Funds stay in your account until spent - FB",
      "Use balance for Market Price Analysis searches - FB",
      "Suitable for Market Price Analysis usage - FB",
      "RM 1.00 will be charged for each search - FB",
    ];
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/market-analysis/plans`,
        );

        if (response.data?.success) {
          setPlans(response.data.data || []);
        }
      } catch (error) {
        console.error("Fetch Market Analysis plans error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const redirectToSecurePay = (
    actionUrl: string,
    params: Record<string, string>,
  ) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = actionUrl;

    Object.entries(params).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = String(value ?? "");
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  const handleGetAccess = async (plan: MarketAnalysisPlan) => {
    try {
      const storedSubscriberRaw = localStorage.getItem("subscriber_user");

      if (!storedSubscriberRaw) {
        showModal({
          type: "warning",
          title: "Warning",
          message: "You are not logged in, please login first.",
          confirmText: "OK",
        });
        return;
      }

      let subscriberUser: any = null;

      try {
        subscriberUser = JSON.parse(storedSubscriberRaw);
      } catch (error) {
        showModal({
          type: "error",
          title: "Error",
          message: "Invalid subscriber session. Please login again.",
          confirmText: "OK",
        });
        return;
      }

      const buyerName = subscriberUser?.name || "";
      const buyerEmail = subscriberUser?.email || "";
      const buyerPhone =
        subscriberUser?.phoneNo || subscriberUser?.phoneno || "";
      const subscriberId = subscriberUser?.id;

      if (!buyerName || !buyerEmail || !buyerPhone || !subscriberId) {
        showModal({
          type: "warning",
          title: "Warning",
          message:
            "Subscriber information is incomplete. Please login again or update your profile.",
          confirmText: "OK",
        });
        return;
      }

      setLoading(true);

      const response = await fetch(
        `${import.meta.env.VITE_LYRAZONE_BASE_URL}/market-analysis/payments/securepay/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subscriberId,
            planId: plan.id,
            buyerName,
            buyerEmail,
            buyerPhone,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        showModal({
          type: "error",
          title: "Error",
          message:
            data.message || "Unable to initiate Market Analysis payment.",
          confirmText: "OK",
        });
        return;
      }

      if (data?.formAction && data?.securepayPayload) {
        redirectToSecurePay(data.formAction, data.securepayPayload);
      } else {
        showModal({
          type: "error",
          title: "Failed",
          message: "Invalid payment response received.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Market Analysis top-up error:", error);

      showModal({
        type: "error",
        title: "Failed",
        message: "Unable to start Market Analysis top-up payment.",
        confirmText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStripeGetAccess = async (plan: MarketAnalysisPlan) => {
    try {
      const storedSubscriberRaw = localStorage.getItem("subscriber_user");

      if (!storedSubscriberRaw) {
        showModal({
          type: "warning",
          title: "Warning",
          message: "You are not logged in, please login first.",
          confirmText: "OK",
        });
        return;
      }

      const subscriberUser = JSON.parse(storedSubscriberRaw);

      const buyerName = subscriberUser?.name || "";
      const buyerEmail = subscriberUser?.email || "";
      const buyerPhone =
        subscriberUser?.phoneNo || subscriberUser?.phoneno || "";
      const subscriberId = subscriberUser?.id;

      if (!buyerName || !buyerEmail || !buyerPhone || !subscriberId) {
        showModal({
          type: "warning",
          title: "Warning",
          message:
            "Subscriber information is incomplete. Please login again or update your profile.",
          confirmText: "OK",
        });
        return;
      }

      setLoading(true);

      const response = await fetch(
        `${import.meta.env.VITE_LYRAZONE_BASE_URL}/market-analysis/payments/stripe/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subscriberId,
            planId: plan.id,
            buyerName,
            buyerEmail,
            buyerPhone,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        showModal({
          type: "error",
          title: "Error",
          message: data.message || "Unable to initiate Stripe payment.",
          confirmText: "OK",
        });
        return;
      }

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }

      showModal({
        type: "error",
        title: "Failed",
        message: "Invalid Stripe payment response received.",
        confirmText: "OK",
      });
    } catch (error) {
      console.error("Stripe Market Analysis top-up error:", error);

      showModal({
        type: "error",
        title: "Failed",
        message: "Unable to start Stripe payment.",
        confirmText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="market-analysis-pricing-page">
      <div className="market-analysis-pricing-header">
        <h2>Market Analysis Access</h2>
        <p>
          Top up your account balance and use it whenever you need Market Price
          Analysis. Your funds remain in your subscriber account until spent.
          {targetCurrency !== "MYR" && (
            <p className="market-analysis-currency-note">
              Prices are shown in your selected country currency. Final card
              payment may vary slightly based on the latest exchange rate.
            </p>
          )}
        </p>
      </div>

      {loading ? (
        <div className="market-analysis-pricing-loading">
          Loading pricing plans...
        </div>
      ) : (
        <div className="market-analysis-pricing-grid">
          {plans.map((plan) => (
            <div className="market-analysis-pricing-card" key={plan.id}>
              <h3>{plan.title}</h3>

              <strong>
                {formatLocalCurrencyOnly(plan.amount, { showCode: true })}
              </strong>

              <small className="market-analysis-converted-note">
                Charges per search{" "}
                {formatLocalCurrencyOnly(plan.search_charge_amount || 1, {
                  showCode: true,
                })}
              </small>

              <p>{plan.description}</p>

              <ul>
                {getPlanBulletPoints(plan).map((point, index) => (
                  <li key={`${plan.id}-bullet-${index}`}>{point}</li>
                ))}
              </ul>
              <p>
                {formatLocalCurrencyOnly(plan.search_charge_amount || 1, {
                  showCode: true,
                })}{" "}
                WILL BE CHARGED FOR EACH SEARCH
              </p>

              {storedSubscriberUser.country === "Malaysia" && (
                <button type="button" onClick={() => handleGetAccess(plan)}>
                  Online Banking
                </button>
              )}

              <button type="button" onClick={() => handleStripeGetAccess(plan)}>
                Pay with Card
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketAnalysisPricing;
